import {
    Box,
    IconButton,
    InputBaseComponentProps,
    TextField,
    Tooltip,
} from '@mui/material';
import React, {
    ChangeEvent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { StepResolver, computeSteppedValue } from './numberInputStepping';

interface NumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    /**
     * Stepper increment. Either a constant, or an adaptive function of the
     * current value (e.g. finer steps near small margins). Note that typed
     * input is validated against `precision`, not `step`.
     */
    step?: StepResolver;
    /**
     * When true, the increment/decrement arrows snap the result to the step
     * grid (anchored at `min`, or 0 when unbounded) instead of adding the raw
     * step. This keeps off-grid typed values from drifting off the grid:
     * 0.22 steps to 0.25 / 0.20 rather than 0.27 / 0.17. Typed values are not
     * snapped — only the arrows.
     */
    snapToStep?: boolean;
    min?: number;
    max?: number;
    precision?: number;
    placeholder?: string;
    disabled?: boolean;
    inputProps?: InputBaseComponentProps;
    fullWidth?: boolean;
    label?: ReactNode;
    autoFocus?: boolean;
    error?: string | null;
    /**
     * If true, fires onChange on every keystroke that parses to a valid
     * in-range number. If false (default), onChange fires only on blur or
     * Enter — useful when intermediate edits shouldn't drive downstream
     * work, but a poor fit for cases where the input is the sole control
     * driving a live preview (the user shouldn't have to blur to see the
     * effect of their edit).
     */
    commitOnChange?: boolean;
    'data-testid'?: string;
}

const roundToPrecision = (num: number, precision: number = 10) => {
    const factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
};

const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    step = 1,
    snapToStep = false,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    precision = 2,
    placeholder = '',
    disabled = false,
    inputProps,
    label,
    fullWidth = false,
    autoFocus = false,
    error,
    commitOnChange = false,
    'data-testid': dataTestId,
}) => {
    const intl = useIntl();
    const [internalValue, setInternalValue] = useState<string>('');
    const [isTouched, setIsTouched] = useState(false);

    // Keep a ref of the latest typed string so the value-change effect
    // below can read it without re-running on every keystroke.
    const internalValueRef = useRef(internalValue);
    internalValueRef.current = internalValue;

    // Reset editing state when the external value changes from outside
    // the input (e.g., parent resets the form, or a different field
    // updates the bound state). Skipped when the new value matches the
    // user's in-progress edit numerically — in commitOnChange mode our
    // own onChange call propagates back into `value` and we don't want
    // that to clobber the typed display ("1." → 1 → ".1.0").
    useEffect(() => {
        const internalAsNum = parseFloat(internalValueRef.current);
        if (isNaN(internalAsNum) || internalAsNum !== value) {
            setIsTouched(false);
        }
    }, [value]);

    // Show prop value when not editing, internal state when editing
    const displayValue = isTouched
        ? internalValue
        : value === null || value === undefined
          ? ''
          : value.toFixed(precision);

    const validateValue = (val: string): boolean => {
        const num = parseFloat(val);
        return !isNaN(num) && num >= min && num <= max;
    };

    const handleFocus = () => {
        // Seed the editing buffer from the current formatted value so the user
        // starts editing from the correct number, not a stale initialisation.
        setInternalValue(displayValue);
    };

    /** Clamp a number to [min, max] and round to the input's precision. */
    const clamp = (v: number) =>
        Math.max(Math.min(roundToPrecision(v, precision), max), min);

    /** Fire onChange only when the new value differs from the current prop. */
    const commitIfChanged = (newValue: number) => {
        if (newValue !== value) {
            onChange(newValue);
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        if (
            newValue === '' ||
            new RegExp(`^-?\\d*\\.?\\d{0,${precision}}$`).test(newValue)
        ) {
            setInternalValue(newValue);
            setIsTouched(true);
            if (commitOnChange && validateValue(newValue)) {
                commitIfChanged(clamp(parseFloat(newValue)));
            }
        }
    };

    // Grid origin the snap lattice is measured from: the lower bound when
    // finite, otherwise 0 (an unbounded input has no natural anchor).
    const stepAnchor = Number.isFinite(min) ? min : 0;

    const handleStep = (direction: 1 | -1) => {
        if (disabled) {
            return;
        }
        const base = value ?? stepAnchor;
        const newValue = clamp(
            computeSteppedValue(base, direction, step, snapToStep, stepAnchor)
        );
        setInternalValue(newValue.toFixed(precision));
        commitIfChanged(newValue);
        setIsTouched(false);
    };

    const handleIncrement = () => handleStep(1);

    const handleDecrement = () => handleStep(-1);

    const handleBlur = () => {
        if (!isTouched) {
            return;
        }

        if (validateValue(internalValue)) {
            commitIfChanged(clamp(parseFloat(internalValue)));
        }
        // Always exit editing mode; the display reverts to the (updated) prop.
        setIsTouched(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            handleBlur();
        }
    };

    const tooltipContent = () => {
        const parts: string[] = [];
        if (min > Number.NEGATIVE_INFINITY) {
            parts.push(`Min: ${min}`);
        }
        if (max < Number.POSITIVE_INFINITY) {
            parts.push(`Max: ${max}`);
        }
        return parts.join(', ');
    };

    return (
        <Tooltip
            open={isTouched}
            placement="bottom-start"
            title={tooltipContent() || ''}
            disableHoverListener={!tooltipContent()}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -12],
                        },
                    },
                ],
            }}
        >
            <Box
                position="relative"
                display="flex"
                alignItems="center"
                data-testid={dataTestId}
            >
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth={fullWidth}
                    value={displayValue}
                    label={label}
                    onFocus={handleFocus}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    error={Boolean(
                        error || (!validateValue(internalValue) && isTouched)
                    )}
                    inputProps={{
                        inputMode: 'decimal',
                        ...inputProps,
                    }}
                />
                <Box display="grid" position="absolute" right={8}>
                    <IconButton
                        size="small"
                        onClick={handleIncrement}
                        disabled={disabled}
                        aria-label={intl.formatMessage({ id: 'increaseValue' })}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9650;
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleDecrement}
                        disabled={disabled}
                        aria-label={intl.formatMessage({ id: 'decreaseValue' })}
                        sx={{
                            fontSize: 8,
                            padding: '1px',
                        }}
                    >
                        &#9660;
                    </IconButton>
                </Box>
            </Box>
        </Tooltip>
    );
};

export default NumberInput;
